import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { usePode } from '@/api/me';
import { configuracoesPermitidas, tituloConfiguracoes } from '@/components/app-shell/navegacao';
import { cn } from '@/lib/utils';

/** Abas cuja propria pagina ja renderiza o h1 (Empresa, Usuarios e acessos). O layout nao repete o h1 nelas. */
const ROTAS_COM_H1_PROPRIO: readonly string[] = ['/configuracoes/empresa', '/configuracoes/usuarios', '/configuracoes/integracoes'];
/** Abas que renderizam o cabecalho inteiro (breadcrumb e h1): o layout nao mostra nem o "Inicio / Configuracoes" nelas. */
const ROTAS_COM_CABECALHO_PROPRIO: readonly string[] = ['/configuracoes/integracoes'];

/** Layout de Configuracoes (⚙ do menu): titulo, breadcrumb e abas (so as permitidas) acima da tela escolhida. */
export const Route = createFileRoute('/_app/configuracoes')({
  component: ConfiguracoesLayout,
});

function ConfiguracoesLayout() {
  const { pode, carregando } = usePode();
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  const abas = configuracoesPermitidas(pode);
  // Segmento inteiro: /configuracoes/empresa nao pode marcar a aba /configuracoes/empresas (e vice-versa).
  const abaAtiva = abas.find((a) => a.rota !== undefined && (caminho === a.rota || caminho.startsWith(`${a.rota}/`)));
  const titulo = tituloConfiguracoes(caminho);
  // Um so h1 por tela: se a aba ja tem o proprio, o layout nao renderiza o dele.
  const h1DaPagina = ROTAS_COM_H1_PROPRIO.some((r) => caminho === r || caminho.startsWith(`${r}/`));
  const cabecalhoProprio = ROTAS_COM_CABECALHO_PROPRIO.some((r) => caminho === r || caminho.startsWith(`${r}/`));

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      {!cabecalhoProprio && (
        <div>
          <div className="text-small text-ink-muted">
            Início <span aria-hidden="true">/</span> Configurações
          </div>
          {!h1DaPagina && <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">{titulo}</h1>}
        </div>
      )}
      {carregando ? (
        <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">
          Carregando configurações…
        </p>
      ) : abas.length === 0 ? (
        <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">
          Sem acesso
        </p>
      ) : (
        <>
          <div role="tablist" aria-label="Seções de configurações" className="flex gap-1 border-b border-border">
            {abas.map((aba) => {
              const selecionada = aba === abaAtiva;
              return (
                <Link
                  key={aba.titulo}
                  role="tab"
                  aria-selected={selecionada}
                  to={aba.rota!}
                  className={cn(
                    '-mb-px border-b-2 px-3 py-2 text-small font-medium',
                    selecionada ? 'border-primary text-ink-body' : 'border-transparent text-ink-muted hover:text-ink-body',
                  )}
                >
                  {aba.titulo}
                </Link>
              );
            })}
          </div>
          <Outlet />
        </>
      )}
    </div>
  );
}
